document.addEventListener('DOMContentLoaded', () => {
  const galleries = Array.prototype.slice.call(
    document.querySelectorAll('[data-olympiad-gallery]')
  );

  galleries.forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-slide]'));
    const dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-dot]'));
    const previousButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const status = gallery.querySelector('[data-gallery-status]');

    if (!track || !viewport || slides.length < 2) {
      return;
    }

    const slideCount = slides.length;
    const requestedStart = parseInt(gallery.getAttribute('data-gallery-start'), 10);
    const startIndex = !isNaN(requestedStart)
      ? Math.min(Math.max(requestedStart, 0), slideCount - 1)
      : 0;
    const reducedMotion = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };
    const requestFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (callback) => window.setTimeout(callback, 16);
    const cancelFrame = typeof window.cancelAnimationFrame === 'function'
      ? window.cancelAnimationFrame.bind(window)
      : window.clearTimeout.bind(window);
    const autoplayDelay = 5000;
    let currentIndex = startIndex;
    let virtualIndex = slideCount + startIndex;
    let autoplayTimer = null;
    let transitionFallbackTimer = null;
    let resizeFrame = null;
    let pointerStartX = null;
    let pointerId = null;
    let isHovered = false;
    let hasFocus = false;

    const modulo = (value) => ((value % slideCount) + slideCount) % slideCount;

    const createClone = (slide) => {
      const clone = slide.cloneNode(true);
      const image = clone.querySelector('img');

      clone.dataset.galleryClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('aria-current');
      clone.removeAttribute('aria-label');

      if (image) {
        image.alt = '';
        image.loading = 'lazy';
      }

      return clone;
    };

    const beforeSlides = document.createDocumentFragment();
    const afterSlides = document.createDocumentFragment();

    slides.forEach((slide) => {
      beforeSlides.appendChild(createClone(slide));
      afterSlides.appendChild(createClone(slide));
    });

    track.insertBefore(beforeSlides, track.firstChild);
    track.appendChild(afterSlides);

    const getStep = () => {
      const slideWidth = slides[0].getBoundingClientRect().width;
      const trackStyles = window.getComputedStyle(track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      return slideWidth + gap;
    };

    const setPosition = (animate = true) => {
      const transform = `translateX(-${virtualIndex * getStep()}px)`;

      if (animate) {
        track.classList.remove('is-jumping');
        track.style.transform = transform;
        return;
      }

      track.classList.add('is-jumping');
      track.getBoundingClientRect();
      track.style.transform = transform;
      track.getBoundingClientRect();

      requestFrame(() => track.classList.remove('is-jumping'));
    };

    const updateState = (announce = false) => {
      slides.forEach((slide, index) => {
        if (index === currentIndex) {
          slide.setAttribute('aria-current', 'true');
        } else {
          slide.removeAttribute('aria-current');
        }
      });

      dots.forEach((dot, index) => {
        if (index === currentIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (announce && status) {
        status.textContent = `Фотография ${currentIndex + 1} из ${slideCount}`;
      }
    };

    const shouldAutoplay = () => {
      const body = document.body;

      return !reducedMotion.matches
        && body.dataset.accessibility !== 'true'
        && body.dataset.noImages !== 'true'
        && !document.hidden
        && !isHovered
        && !hasFocus;
    };

    const stopAutoplay = () => {
      if (autoplayTimer !== null) {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const startAutoplay = () => {
      stopAutoplay();

      if (!shouldAutoplay()) {
        return;
      }

      autoplayTimer = window.setTimeout(() => {
        moveBy(1, false);
      }, autoplayDelay);
    };

    const normalizeVirtualIndex = () => {
      if (virtualIndex < slideCount || virtualIndex >= slideCount * 2) {
        virtualIndex = slideCount + currentIndex;
        setPosition(false);
      }
    };

    const scheduleNormalization = () => {
      if (transitionFallbackTimer !== null) {
        window.clearTimeout(transitionFallbackTimer);
      }

      transitionFallbackTimer = window.setTimeout(() => {
        transitionFallbackTimer = null;
        normalizeVirtualIndex();
      }, 550);
    };

    const moveBy = (distance, announce = true) => {
      normalizeVirtualIndex();
      virtualIndex += distance;
      currentIndex = modulo(virtualIndex);
      setPosition();
      updateState(announce);
      scheduleNormalization();
      startAutoplay();
    };

    const showSlide = (nextIndex, announce = true) => {
      const normalizedIndex = modulo(nextIndex);
      const candidates = [
        normalizedIndex,
        slideCount + normalizedIndex,
        slideCount * 2 + normalizedIndex
      ];

      virtualIndex = candidates.reduce((closest, candidate) => (
        Math.abs(candidate - virtualIndex) < Math.abs(closest - virtualIndex)
          ? candidate
          : closest
      ));
      currentIndex = normalizedIndex;
      setPosition();
      updateState(announce);
      scheduleNormalization();
      startAutoplay();
    };

    track.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') {
        if (transitionFallbackTimer !== null) {
          window.clearTimeout(transitionFallbackTimer);
          transitionFallbackTimer = null;
        }

        normalizeVirtualIndex();
      }
    });

    if (previousButton) {
      previousButton.addEventListener('click', () => moveBy(-1));
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => moveBy(1));
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        showSlide(Number(dot.dataset.galleryDot));
      });
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBy(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveBy(1);
      }
    });

    gallery.addEventListener('mouseenter', () => {
      isHovered = true;
      stopAutoplay();
    });

    gallery.addEventListener('mouseleave', () => {
      isHovered = false;
      startAutoplay();
    });

    gallery.addEventListener('focusin', () => {
      hasFocus = true;
      stopAutoplay();
    });

    gallery.addEventListener('focusout', () => {
      requestFrame(() => {
        hasFocus = gallery.contains(document.activeElement);
        startAutoplay();
      });
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (event.target.closest && event.target.closest('button')) {
        return;
      }

      pointerStartX = event.clientX;
      pointerId = event.pointerId;

      if (typeof viewport.setPointerCapture === 'function') {
        viewport.setPointerCapture(event.pointerId);
      }

      stopAutoplay();
    });

    viewport.addEventListener('pointerup', (event) => {
      if (pointerStartX === null || pointerId !== event.pointerId) {
        return;
      }

      const distance = event.clientX - pointerStartX;

      if (Math.abs(distance) >= 40) {
        moveBy(distance < 0 ? 1 : -1);
      } else {
        startAutoplay();
      }

      pointerStartX = null;
      pointerId = null;
    });

    viewport.addEventListener('pointercancel', () => {
      pointerStartX = null;
      pointerId = null;
      startAutoplay();
    });

    setPosition(false);
    updateState();
    startAutoplay();

    const handleResponsiveChange = () => {
      if (resizeFrame !== null) {
        cancelFrame(resizeFrame);
      }

      resizeFrame = requestFrame(() => {
        resizeFrame = null;
        setPosition(false);
      });
    };

    if (typeof window.ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(handleResponsiveChange);
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener('resize', handleResponsiveChange);
    }

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', startAutoplay);
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(startAutoplay);
    }

    document.addEventListener('visibilitychange', startAutoplay);

    if (typeof window.MutationObserver === 'function') {
      const accessibilityObserver = new MutationObserver(startAutoplay);
      accessibilityObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-accessibility', 'data-no-images']
      });
    }

  });
});
